import { useEffect } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { CONTACT_EMAIL, LEGAL_UPDATED, STORAGE_NAME } from "../global";
import "./Legal.css";

const SAVED_KEY = `${STORAGE_NAME}:saved`;

function Privacy() {
  useEffect(() => {
    document.title = "Confidentialité — Paris Agenda";
    return () => {
      document.title = "Paris Agenda";
    };
  }, []);

  return (
    <div className="container pa-legal">
      <PageHeader
        eyebrow="Vos données"
        title="Politique de confidentialité"
        intro="Paris Agenda fonctionne sans compte, sans cookie et sans mesure d'audience. Cette page détaille précisément ce que cela implique."
      />

      <div className="pa-legal__summary">
        <h2>En résumé</h2>
        <ul className="pa-legal__list">
          <li>
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            <span>Paris Agenda ne dépose ni ne lit aucun cookie.</span>
          </li>
          <li>
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            <span>
              Aucun traqueur, aucune mesure d'audience, aucune publicité.
            </span>
          </li>
          <li>
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            <span>
              Aucun compte, aucun formulaire&nbsp;: le site ne vous demande
              jamais d'information personnelle.
            </span>
          </li>
          <li>
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            <span>
              Vos favoris restent dans votre navigateur et ne sont jamais
              transmis.
            </span>
          </li>
          <li>
            <i className="bi bi-check-lg" aria-hidden="true"></i>
            <span>
              Les polices de caractères sont servies par le site lui-même&nbsp;:
              afficher une page n'appelle aucun service de Google.
            </span>
          </li>
        </ul>
      </div>

      <div className="pa-prose">
        <h2>Responsable du traitement</h2>
        <p>
          Yanis ARENES, éditeur du site. Pour toute question relative à cette
          page&nbsp;: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>

        <h2>Ce qui est enregistré sur votre appareil</h2>
        <p>
          Lorsque vous enregistrez un événement en favori, le site conserve son
          identifiant dans le stockage local de votre navigateur, sous la clé{" "}
          <code>{SAVED_KEY}</code>. Cette liste ne contient que des
          identifiants d'événements — aucun nom, aucune adresse, aucun
          identifiant publicitaire. Elle n'est lisible que par votre navigateur,
          n'est envoyée à aucun serveur, et l'éditeur n'y a jamais accès.
        </p>
        <p>
          Vous pouvez l'effacer à tout moment&nbsp;: le bouton «&nbsp;Vider la
          sélection&nbsp;» en bas de la page{" "}
          <Link to="/saved">Mes favoris</Link> la supprime, et vider les données
          de site depuis les réglages de votre navigateur produit le même
          résultat.
        </p>
        <p>
          Ce stockage local n'est pas un cookie&nbsp;: il ne suit pas votre
          navigation et n'est transmis dans aucune requête. À ce titre, il ne
          requiert pas de bandeau de consentement.
        </p>

        <h2>Services tiers sollicités par le site</h2>
        <p>
          Afficher une page implique de contacter les services ci-dessous. Comme
          pour toute requête sur le web, chacun reçoit alors votre adresse IP et
          des en-têtes techniques (navigateur, langue). L'éditeur ne reçoit ni
          ne conserve ces informations.
        </p>
        <ul>
          <li>
            <strong>opendata.paris.fr</strong> — Ville de Paris, via la
            plateforme OpenDataSoft. Contacté à chaque recherche et à chaque
            fiche consultée, pour récupérer les événements.
          </li>
          <li>
            <strong>tile.openstreetmap.org</strong> — OpenStreetMap Foundation.
            Contacté uniquement lorsque vous ouvrez une fiche comportant une
            carte, pour en charger le fond.
          </li>
          <li>
            <strong>Firebase Hosting</strong> — Google Ireland Limited. Livre
            les fichiers du site et tient des journaux de serveur techniques.
          </li>
        </ul>
        <p>
          Les boutons de partage n'activent rien tant que vous ne cliquez pas.
          Si vous choisissez WhatsApp, Facebook, X ou votre messagerie, c'est
          seulement à ce moment que le service correspondant est contacté, selon
          ses propres règles de confidentialité.
        </p>

        <h2>Transferts hors de l'Union européenne</h2>
        <p>
          Les requêtes servies par l'hébergeur peuvent être traitées en dehors
          de l'Union européenne, selon l'infrastructure de ce prestataire. Les
          polices de caractères étant servies par le site lui-même, elles
          n'occasionnent aucun transfert. L'éditeur n'opère pour sa part aucun
          transfert de données.
        </p>

        <h2>Durée de conservation</h2>
        <p>
          L'éditeur ne conservant aucune donnée vous concernant, aucune durée de
          conservation ne s'applique de son côté. La liste de vos favoris reste
          dans votre navigateur jusqu'à ce que vous la supprimiez.
        </p>

        <h2>Vos droits</h2>
        <p>
          Le règlement général sur la protection des données vous garantit un
          droit d'accès, de rectification, d'effacement, de limitation et
          d'opposition sur vos données personnelles. Paris Agenda ne collectant
          ni ne conservant aucune donnée personnelle, ces droits n'ont en
          pratique pas d'objet à l'égard de l'éditeur — il n'existe aucun
          dossier à consulter ou à supprimer.
        </p>
        <p>
          Pour toute question, écrivez à{" "}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Vous pouvez
          également introduire une réclamation auprès de la{" "}
          <a
            href="https://www.cnil.fr/fr/plaintes"
            target="_blank"
            rel="noopener noreferrer"
          >
            CNIL
          </a>
          .
        </p>

        <h2>Évolution de cette page</h2>
        <p>
          Si le site venait à intégrer un service modifiant ce qui précède — une
          mesure d'audience, par exemple — cette page serait mise à jour avant
          sa mise en ligne.
        </p>
      </div>

      <p className="pa-legal__sibling">
        Voir aussi&nbsp;:{" "}
        <Link to="/mentions-legales" className="pa-link">
          Mentions légales
        </Link>
      </p>

      <p className="pa-legal__updated">
        Dernière mise à jour&nbsp;: {LEGAL_UPDATED}.
      </p>
    </div>
  );
}

export default Privacy;
